import { type ServiceParams, unwrapJson } from "../client";
import type {
  CreateProviderCredentialRequest,
  ListProviderCredentialsFilters,
  ProviderCredentialListResponse,
  ProviderCredentialResponse,
  UpdateProviderCredentialRequest,
} from "./types";

/**
 * Providers service — wraps the BYOK tenant-credential admin
 * endpoints under ``/v1/admin/providers``. SAC closure pattern
 * matches the rest of the FE service layer; no axios, no shared
 * state libraries.
 *
 * Mutation paths (POST + PATCH rotation) carry the plaintext key
 * back to the caller exactly once via ``api_key_plaintext``. The
 * tab UI surfaces that field in a modal-locked show-once dialog
 * (mirrors the FE-2 API-keys + FE-4 signup-keys pattern). Empty
 * strings on metadata-only PATCH are normalised by
 * :func:`extractRotatedKey` so the show-once dialog never opens
 * without a real key to display.
 */
const Providers = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/admin/providers`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async create(
      payload: CreateProviderCredentialRequest
    ): Promise<ProviderCredentialResponse> {
      const response = await fetch(base, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<ProviderCredentialResponse>(response);
    },

    async list(
      filters: ListProviderCredentialsFilters = {}
    ): Promise<ProviderCredentialListResponse> {
      const response = await fetch(
        `${base}${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<ProviderCredentialListResponse>(response);
    },

    async getDetail(credentialId: string): Promise<ProviderCredentialResponse> {
      const response = await fetch(`${base}/${credentialId}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<ProviderCredentialResponse>(response);
    },

    async update(
      credentialId: string,
      payload: Omit<UpdateProviderCredentialRequest, "api_key">
    ): Promise<ProviderCredentialResponse> {
      const response = await fetch(`${base}/${credentialId}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<ProviderCredentialResponse>(response);
    },

    async rotateKey(
      credentialId: string,
      newApiKey: string
    ): Promise<ProviderCredentialResponse> {
      const response = await fetch(`${base}/${credentialId}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify({ api_key: newApiKey }),
      });
      return unwrapJson<ProviderCredentialResponse>(response);
    },

    async revoke(credentialId: string): Promise<void> {
      const response = await fetch(`${base}/${credentialId}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok) return unwrapJson<void>(response);
    },

    async refreshModels(
      credentialId: string
    ): Promise<ProviderCredentialResponse> {
      const response = await fetch(
        `${base}/${credentialId}/refresh-models`,
        { method: "POST", headers: config() }
      );
      return unwrapJson<ProviderCredentialResponse>(response);
    },
  };
};

export default Providers;
